<template>
  <v-container fluid class="d-flex flex-column fill-height">
    <div @click="showPermissionProductDialog">
      <v-card
        tile
        flat
        class="elevation-0 overflow-y-auto ma-auto"
        max-width="90vw"
        height="calc(89vh - 80px)"
        :loading="loading"
      >
        <v-card-title class="pa-2 mt-5">
          <span class="mr-4">{{ $t("common.userInfos") }} </span>
          <TooltipIcon
            right
            color="info"
            icon="ph-fill ph-info"
            :label="$t('userInformations.userInfosDescription')"
          />
        </v-card-title>
        <v-form
          ref="form"
          v-model="valid"
          @submit.prevent=""
          class="mt-2 mb-5"
          :disabled="!hasPermission"
        >
          <v-row class="ma-0 pa-0">
            <v-col
              v-for="tag in contactTags"
              :key="tag"
              cols="12"
              sm="12"
              md="8"
              lg="6"
              class="d-flex"
            >
              <v-col
                cols="7"
                sm="5"
                md="6"
                lg="6"
                align-self="center"
                class="ma-0 pa-0"
              >
                <v-card-text
                  class="font-weight-medium body-2 py-0 pl-2 d-flex align-center"
                  >{{ $t(userEditableTags[tag].translatePath) }}
                </v-card-text>
                <v-card-text class="body-2 py-0 pl-2">{{
                  userEditableTags[tag].label
                }}</v-card-text>
              </v-col>

              <v-col
                class="text-left"
                align-self="start"
                cols="5"
                sm="7"
                md="12"
                lg="6"
              >
                <v-text-field
                  v-model="tagsEdit[tag]"
                  class="pt-0 pr-2 my-0 body-2"
                  autocomplete="off"
                  hide-details="auto"
                  clearable
                  :label="
                    userEditableTags[tag].labelGoogle ||
                    $t(userEditableTags[tag].translatePath) ||
                    userEditableTags[tag].keyName
                  "
                  :rules="getRules(tag)"
                  @input="saveDataOnLocalStorage"
                  @change="saveDataOnLocalStorage"
                ></v-text-field>
              </v-col>
            </v-col>
          </v-row>
          <v-row v-for="(tag, i) in employeeTags" :key="i" class="ma-0 pa-0">
            <v-col cols="12" sm="" lg="3" align-self="center">
              <v-card-text
                class="font-weight-medium body-2 py-0 pl-2 d-flex align-center"
              >
                <span class="mr-4">{{
                  $t(userEditableTags[tag].translatePath)
                }}</span>
                <TooltipIcon
                  v-if="tag === 'sign_photo'"
                  right
                  color="info"
                  icon="ph-fill ph-info"
                  :label="$t('userInformations.photoTooltipText')"
                />
              </v-card-text>
              <v-card-text class="body-2 py-0 pl-2">{{
                userEditableTags[tag].label
              }}</v-card-text>
            </v-col>

            <v-col class="text-left" align-self="start" cols="12" sm="7" lg="9">
              <div v-if="userEditableTags[tag].label === '[*USER_SIGN_PHOTO*]'">
                <v-avatar
                  v-if="!hasAcceptedDriveScopes"
                  size="60"
                  class="mb-4"
                  v-ripple
                >
                  <img v-if="!tagsEdit[tag]" :src="getPreviewTagImage" />
                  <img v-else :src="tagsEdit[tag]" />
                </v-avatar>

                <div v-else class="d-flex align-center mb-4">
                  <v-badge
                    bordered
                    bottom
                    color="accent"
                    offset-x="15"
                    offset-y="40"
                    icon="ph-pencil-simple"
                  >
                    <v-avatar
                      @click="openUploadImageDialog(tag, tagsEdit[tag])"
                      size="60"
                      class="mb-6 avatar"
                      v-ripple
                    >
                      <v-tooltip left>
                        <template v-slot:activator="{ on, attrs }">
                          <img
                            v-if="!tagsEdit[tag]"
                            v-bind="attrs"
                            v-on="on"
                            :src="getPreviewTagImage"
                          />
                          <img
                            v-else
                            :src="tagsEdit[tag]"
                            v-bind="attrs"
                            v-on="on"
                          />
                        </template>
                        <span>{{ $t("userInformations.addImage") }}</span>
                      </v-tooltip>
                    </v-avatar>
                  </v-badge>

                  <div
                    class="ml-6 d-flex flex-column"
                    v-if="!isCompanyMicrosoft"
                  >
                    <v-btn
                      elevation="0"
                      outlined
                      text
                      small
                      class="text-none mb-4"
                      @click="openUploadImageDialog(tag, tagsEdit[tag])"
                    >
                      <v-icon left dark> ph-cloud-arrow-up </v-icon>
                      {{ $t("userInformations.upload") }}
                    </v-btn>
                  </div>
                </div>

                <v-text-field
                  color="accent"
                  v-model="tagsEdit[tag]"
                  @input="updateURL(tag, $event)"
                  class="pt-0 pr-2 my-0 body-2"
                  autocomplete="off"
                  clearable
                  :label="userEditableTags[tag].label"
                  @blur="checkPhotoLink"
                  :error-messages="photoLinkError"
                ></v-text-field>
              </div>

              <v-text-field
                v-else
                v-model="tagsEdit[tag]"
                class="pt-0 pr-2 my-0 body-2"
                autocomplete="off"
                clearable
                :label="
                  userEditableTags[tag].labelGoogle ||
                  $t(userEditableTags[tag].translatePath) ||
                  userEditableTags[tag].keyName
                "
                :rules="[
                  tag === 'calendar_schedules_link'
                    ? rules.maxCalendarSchedulesLink
                    : rules.maxUserInfo,
                ]"
                :persistent-hint="userEditableTags[tag]?.hint ? true : false"
                :hint="
                  $t(userEditableTags[tag].translateHintPath) ||
                  userEditableTags[tag]?.hint
                "
                @input="saveDataOnLocalStorage"
                @change="saveDataOnLocalStorage"
              >
                <template v-slot:message="{ message }">
                  <div
                    style="color: #1948a3"
                    class="d-flex align-center"
                    v-if="
                      userEditableTags[tag].label == '[*MEETING_SCHEDULE_LINK*]'
                    "
                  >
                    <v-icon small class="mr-1" color="#2196f3"
                      >ph-fill ph-info</v-icon
                    >
                    <span v-html="message" />
                  </div>
                  <span v-else v-html="message" />
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <v-card-title class="pa-2 mt-5">
            <span class="mr-4">{{ $t("common.customizedInformation") }}</span>
          </v-card-title>
          <v-row v-for="tag in userCustomTags" :key="tag" class="ma-0 pa-0">
            <v-col cols="12" sm="4" lg="3" align-self="center">
              <v-card-text
                class="font-weight-medium body-2 py-0 pl-2 d-flex align-center"
                ><span class="mr-4">
                  {{ $t(userEditableTags[tag].translatePath) }}
                </span>
                <TooltipIcon
                  v-if="userEditableTags[tag].info"
                  right
                  color="info"
                  icon="ph-fill ph-info"
                  :label="$t(userEditableTags[tag].infoTranslatePath)"
                />
              </v-card-text>
              <v-card-text class="body-2 py-0 pl-2">{{
                userEditableTags[tag].label
              }}</v-card-text>
            </v-col>

            <v-col class="text-left" align-self="start" cols="12" sm="7" lg="9">
              <v-text-field
                :data-testid="`${tag}`"
                v-model="tagsEdit[tag]"
                hide-details="auto"
                @input="updateURL(tag, $event)"
                class="pt-0 pr-2 my-0 body-2"
                autocomplete="off"
                :placeholder="
                  $t(userEditableTags[tag].translatePlaceholderPath)
                "
                :label="$t(userEditableTags[tag].translatePath)"
                @change="saveDataOnLocalStorage"
                clearable
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row v-for="(tag, i) in customTagsEdit" :key="i" class="ma-0 pa-0">
            <v-col
              cols="12"
              sm="4"
              lg="3"
              align-self="start"
              class="px-0 mx-0 d-flex align-end"
            >
              <div>
                <v-card-text class="font-weight-medium body-2 py-0 ml-1"
                  >{{ tag.name }}
                </v-card-text>
                <v-card-text
                  class="grey--text text--darken-2 body-2 py-0 ml-1"
                  :style="
                    $vuetify.breakpoint.mdAndDown && $vuetify.breakpoint.smAndUp
                      ? 'inline-size: 183px'
                      : ''
                  "
                  >{{ tag.tag }}</v-card-text
                >
              </div>
            </v-col>
            <v-col cols="12" sm="7" lg="9" align-self="end">
              <v-text-field
                :placeholder="tag.name"
                @input="$forceUpdate(), saveDataOnLocalStorage"
                v-model="tag.value"
                hide-details="auto"
                color="grey"
                class="pt-0 my-0 body-2"
                clearable
                :rules="tagRules"
                @change="saveDataOnLocalStorage"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </div>

    <v-divider style="width: 90vw"></v-divider>
    <v-footer
      rounded
      style="height: 70px"
      color="background"
      class="border-bottom rounded-xl mt-3"
      width="90vw"
    >
      <v-btn
        text
        height="52"
        color="secondary"
        style="border: 2px solid grey !important"
        class="mx-auto text-none text-body-1 font-weight-medium px-8"
        outlined
        @click="$emit('back')"
        :disabled="loading"
      >
        <v-icon right v-text="'ph-arrow-left'" class="mr-4" size="22" />
        {{ $t("common.back") }}
      </v-btn>

      <v-spacer />
      <div>
        <v-btn
          height="52"
          color="accent"
          class="mx-auto text-none text-body-1 font-weight-bold px-8 mr-0"
          @click="handleSave"
          :disabled="loading || !valid || !hasPermission || loadingUser"
          :loading="loading || loadingUser"
        >
          {{ $t("action.next") }}
          <v-icon right v-text="'ph-arrow-right'" class="ml-4" size="22" />
        </v-btn>
      </div>
    </v-footer>

    <HeaderDialog
      id="theweekend"
      :title="$t('userInformations.addImage')"
      color="primary"
      :show="showUploadImageDialog"
      :loading="uploadingImageLoading || loadingUser"
      width="700"
      :disabledAction="!file || loadingUser"
      showCancel
      :closeText="$t('action.cancel')"
      button-class="text-none text-subtitle-1 font-weight-medium elevation-0"
      @close="closeImageUploadDialog"
      @action="uploadImage"
    >
      <template v-slot:body>
        <v-row class="flex-column justify-center">
          <v-avatar size="200" class="mb-4 mt-8 mx-auto">
            <img v-if="!imageURLPreview" :src="getPreviewTagImage" />
            <img v-else :src="imageURLPreview" />
          </v-avatar>

          <div class="mx-4">
            <v-file-input
              accept="image/*"
              :label="$t('userInformations.selectImage')"
              :hint="$t('userInformations.imageSavedOnDrive')"
              persistent-hint
              @change="handleFileUpload"
            ></v-file-input>
          </div>
        </v-row>
      </template>
    </HeaderDialog>
  </v-container>
</template>
<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { changeDriveURL, isPublicLink } from "@/helpers/services/images";
import { setFirebaseEvent } from "@/helpers/services/registerAnalytics.js";
import {
  errorMessages,
  successMessages,
} from "@/helpers/variables/snackbarMessages";
import { userTags, userEditableTags } from "@/helpers/variables/tags";
import { INCREMENTAL_SCOPES } from "@/helpers/variables/scopes";

export default {
  name: "UpdateUserSignatureInfo",

  data() {
    return {
      userEditableTags,
      valid: true,
      tagsEdit: {},
      customTagsEdit: {},
      loading: false,
      rules: {
        maxPhone: (v) =>
          v?.length <= 20 || this.$t("textFieldRules.max20characters"),
        maxUserInfo: (v) =>
          v?.length <= 60 || this.$t("textFieldRules.max60characters"),
        maxCalendarSchedulesLink: (v) =>
          String(v)?.length <= 120 ||
          this.$t("textFieldRules.max120characters"),
      },
      tagRules: [
        (v) =>
          String(v)?.length <= 1490 ||
          this.$t("textFieldRules.max1490characters"),
      ],
      file: null,
      userSignatureInfoStep: 3,
      uploadingImageLoading: false,
      showUploadImageDialog: false,
      imageURLPreview: null,
      selectedTagImage: null,
      selectedTagImageURL: null,
      photoLinkError: "",
    };
  },

  computed: {
    ...mapGetters([
      "usersCustomTags",
      "usersCustomTagKeys",
      "mainDomain",
      "token",
      "companyHasSign",
      "currentOnboardingStep",
      "hasAcceptedDriveScopes",
      "scopeCode",
      "isCompanyMicrosoft",
      "onboardingUser",
      "onboardingForms",
    ]),

    auth() {
      return {
        headers: {
          Authorization: this.token,
        },
      };
    },

    getPreviewTagImage() {
      return this.selectedTagImageURL || require("@/assets/user-default.png");
    },

    thereAreChanges() {
      return Object.keys(this.payload).some((key) => {
        const payloadValue = this.payload[key];
        const onboardingValue = this.onboardingUser[key];
        return (
          (payloadValue || onboardingValue) && payloadValue != onboardingValue
        );
      });
    },

    customTags() {
      return this.onboardingUser.custom_tags || {};
    },

    hasPermission() {
      const not_valid_status = [
        "PLAN_TRIAL_ENDED",
        "PRODUCT_DOES_NOT_EXIST_IN_PLANS",
      ];
      if (not_valid_status.includes(this.companyHasSign)) return false;
      else return true;
    },

    contactTags() {
      const { phone, mobile, whatsapp_number, departament, position, ramal } =
        this.userEditableTags;
      return Object.keys({
        phone,
        mobile,
        whatsapp_number,
        departament,
        position,
        ramal,
      });
    },

    employeeTags() {
      const { sign_photo, calendar_schedules_link } = this.userEditableTags;

      return Object.keys({
        sign_photo,
        calendar_schedules_link,
      });
    },

    userCustomTags() {
      const { user_custom_1, user_custom_2 } = this.userEditableTags;

      return Object.keys({
        user_custom_1,
        user_custom_2,
      });
    },

    loadingUser() {
      return !this.onboardingUser.key;
    },

    payload() {
      let payload = {};

      Object.keys(this.tagsEdit).forEach((tag) => {
        if (this.tagsEdit[tag] !== this.onboardingUser[tag] && tag != "photo") {
          payload[tag] = this.tagsEdit[tag];
        }
      });

      let customTagsEdited = false;

      const { custom_tags } = this.onboardingUser;

      if (!custom_tags) {
        return payload;
      }
      this.usersCustomTagKeys.forEach((tag) => {
        if (this.customTagsEdit[tag]?.value != custom_tags[tag]?.value) {
          if (!payload.custom_tags) {
            payload.custom_tags = {};
          }

          payload.custom_tags[tag] = this.customTagsEdit[tag];

          customTagsEdited = true;
        }
      });

      if (!customTagsEdited) {
        delete payload.custom_tags;
      }

      return payload;
    },
  },

  methods: {
    ...mapMutations([
      "setSnackBar",
      "setPermissionDialog",
      "setAcceptedDriveScopes",
      "setOnboardingUser",
      "setOnboardingForms",
    ]),
    ...mapActions(["updateUserSignatureInfo", "checkScopes"]),

    handleFileUpload(imageFile) {
      if (imageFile) {
        this.imageURLPreview = URL.createObjectURL(imageFile);
      }
      this.file = imageFile;
    },

    closeImageUploadDialog() {
      this.showUploadImageDialog = false;
      this.imageURLPreview = null;
      this.file = null;
      this.selectedTagImage = null;
      this.selectedTagImageURL = null;
    },

    uploadImage() {
      if (!this.file) return;

      this.uploadingImageLoading = true;
      const formData = new FormData();
      formData.append("image", this.file);
      formData.append("image_field_key", "sign_photo");

      const baseUrl = `${process.env.VUE_APP_API_BASE_URL}/users`;

      const url = `${baseUrl}/${this.onboardingUser.key}/upload-image`;

      this.$axios
        .post(url, formData, this.auth)
        .then(({ data }) => {
          this.tagsEdit[this.selectedTagImage] = data.shared_drive_url;
        })
        .catch((error) => {
          console.error("Error uploading image:", error);
          this.setSnackBar({
            message: errorMessages["FAILED_TO_UPLOAD_IMAGE"],
            color: "error",
            show: true,
            timeout: 8000,
          });
        })
        .finally(() => {
          this.uploadingImageLoading = false;
          this.closeImageUploadDialog();
        });
    },

    openUploadImageDialog(tag, tagImageURL) {
      this.selectedTagImage = tag;
      this.selectedTagImageURL = tagImageURL;
      this.showUploadImageDialog = true;
    },

    saveAnalytics(log) {
      if (this.mainDomain) {
        setFirebaseEvent(this.mainDomain, log.event, log.click);
      }
    },

    saveDataOnLocalStorage() {
      this.setOnboardingForms({
        label: "tags",
        formData: this.tagsEdit,
      });
      this.setOnboardingForms({
        label: "customTags",
        formData: this.customTagsEdit,
      });
    },

    loadDataFromLocalStorage() {
      const tagsFormData = this.onboardingForms.tags;
      if (tagsFormData) {
        for (let key in this.tagsEdit) {
          this.tagsEdit[key] = tagsFormData[key];
        }
      }
      const customTagsFormData = this.onboardingForms.customTags;
      if (customTagsFormData) {
        for (let key in this.customTagsEdit) {
          this.customTagsEdit[key] = customTagsFormData[key];
        }
      }
    },

    setCustomTagsEdit() {
      const customTagsEdit = this.usersCustomTagKeys.reduce((acc, tagKey) => {
        const customTag = this.usersCustomTags[tagKey];
        customTag.value = this.customTags[tagKey]?.value || "";
        acc[tagKey] = customTag;

        return acc;
      }, {});

      this.customTagsEdit = customTagsEdit;
    },

    setTagsEdit() {
      this.tagsEdit = userTags.reduce((a, e) => {
        if (e === "sign_photo") {
          a[e] = changeDriveURL(this.onboardingUser[e] || "");
        } else {
          a[e] = this.onboardingUser[e] || "";
        }
        return a;
      }, {});
    },

    getRules(tag) {
      const phoneTag = ["phone", "mobile", "whatsapp_number"];
      if (phoneTag.includes(tag)) return [this.rules.maxPhone];
      else return [this.rules.maxUserInfo];
    },

    async handleSave() {
      this.loading = true;
      await this.checkPhotoLink();
      if (this.photoLinkError) {
        this.loading = false;
        return;
      }
      let result = true;
      if (this.thereAreChanges) {
        result = await this.save();
      }
      this.loading = false;
      if (result && this.userSignatureInfoStep === this.currentOnboardingStep) {
        this.$emit("next");
      }
    },

    async save() {
      const payload = {
        id_user: this.onboardingUser.id_google,
        emails: [this.onboardingUser.email],
        editable_data: this.payload,
        workspace_data: this.assignWorkspacePayload(this.payload),
      };

      delete payload.editable_data.email;
      delete payload.editable_data.name;

      const log = {
        event: "update_user_signature_info_onboarding",
        click: "Botão de salvar informações de assinatura do usuário e avançar",
      };
      this.saveAnalytics(log);

      const { tags_update, PLAN_EXPIRED } = errorMessages;

      try {
        const response = await this.updateUserSignatureInfo(payload);
        const { data } = response;
        this.setSnackBar({
          show: true,
          message: successMessages.saves_changes,
          color: "success",
        });
        this.setOnboardingUser({
          ...this.onboardingUser,
          ...data.user,
        });
        return true;
      } catch (error) {
        console.error("updateUserSignatureInfo: ", error);
        let message = tags_update;
        if (error.response.data.code === "PLAN_TRIAL_ENDED") {
          message = PLAN_EXPIRED;
        }
        this.setSnackBar({
          show: true,
          message,
          color: "error",
        });
        return false;
      }
    },

    assignWorkspacePayload(payload) {
      let workspacePayload = [];
      const google_workspace_editable_data = [
        "phone",
        "mobile",
        "departament",
        "position",
      ];
      google_workspace_editable_data.map((workspaceData) => {
        if (workspaceData in payload) {
          workspacePayload.push(workspaceData);
        }
      });
      return workspacePayload;
    },

    updateURL(tag, value) {
      this.tagsEdit[tag] = changeDriveURL(value);
      this.saveDataOnLocalStorage();
    },

    async checkPhotoLink() {
      const url = this.tagsEdit["sign_photo"];
      this.photoLinkError = "";
      if (!url) {
        return;
      }

      const isPublic = await isPublicLink(url);
      if (!isPublic) {
        this.photoLinkError = this.$t("errorMessages.photoLink");
        return;
      }
    },

    showPermissionProductDialog() {
      if (this.hasPermission) return null;
      const dialog = {
        show: true,
        status: "PRODUCT_IN_DISABLED_PLAN",
        app: "conecta_sign",
      };
      this.setPermissionDialog(dialog);
    },
  },
  watch: {
    onboardingUser: {
      handler() {
        this.setTagsEdit();
        this.setCustomTagsEdit();
        this.loadDataFromLocalStorage();
      },
      immediate: true,
    },
  },

  async beforeMount() {
    const driveScope = INCREMENTAL_SCOPES.ADMIN_DRIVE;
    if (!this.scopeCode(driveScope)) {
      const payload = {
        scopes: [driveScope],
      };

      await this.checkScopes(payload)
        .then(() => {
          this.setAcceptedDriveScopes(true);
        })
        .catch(() => {
          this.setAcceptedDriveScopes(false);
        });
    }
    if (!this.hasPermission) {
      this.showPermissionProductDialog();
    }
  },
};
</script>
<style scoped></style>
